import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Icon from "./Icon";
import "./Header.css";

export default function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  // Collapsed-nav panel on narrow screens (see the 980px breakpoint in
  // Header.css). Ignored on desktop, where the nav is always shown.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const closeAll = () => {
    setNavOpen(false);
    setOpenMenu(null);
  };

  // Any navigation closes the panel and dropdowns.
  useEffect(closeAll, [location.pathname]);

  const toggleMenu = (menuName) => {
    setOpenMenu((current) => (current === menuName ? null : menuName));
  };

  // A click/tap outside the header closes everything; one inside the header but
  // outside the dropdowns closes just the dropdown. Escape closes everything.
  useEffect(() => {
    if (!openMenu && !navOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest(".site-header")) {
        closeAll();
      } else if (!event.target.closest(".nav-dropdown")) {
        setOpenMenu(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAll();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu, navOpen]);

  return (
    <header className="site-header">
      <div className="container nav-inner">
        <Link to="/" className="brand">
          <img
            src="/assets/logo-header.png"
            alt="The Bug and Frog Foundation"
            className="brand-logo"
          />
        </Link>

        <button
          type="button"
          className="nav-toggle"
          aria-expanded={navOpen}
          aria-controls="site-nav"
          aria-label={navOpen ? "Close menu" : "Open menu"}
          onClick={() => setNavOpen((open) => !open)}
        >
          <Icon name={navOpen ? "close" : "menu"} size={26} />
        </button>

        <nav
          id="site-nav"
          className={`main-nav${navOpen ? " open" : ""}`}
          // Picking any link closes the panel, even when it's the current page.
          onClick={(event) => {
            if (event.target.closest("a")) closeAll();
          }}
        >
          <NavLink to="/" end className={({ isActive }) => (isActive ? "active" : "")}>
            Home
          </NavLink>

          <div className={`nav-dropdown ${openMenu === "about" ? "open" : ""}`}>
            <button
              type="button"
              className="nav-dropdown-toggle"
              aria-expanded={openMenu === "about"}
              aria-haspopup="menu"
              onClick={() => toggleMenu("about")}
            >
              About us
            </button>
            <div className="nav-dropdown-menu" role="menu">
              <NavLink
                to="/about-us"
                className={({ isActive }) => (isActive ? "active" : "")}
                onClick={() => setOpenMenu(null)}
              >
                About
              </NavLink>
              <NavLink
                to="/who-we-are"
                className={({ isActive }) => (isActive ? "active" : "")}
                onClick={() => setOpenMenu(null)}
              >
                Our founders
              </NavLink>
              <NavLink
                to="/programs"
                className={({ isActive }) => (isActive ? "active" : "")}
                onClick={() => setOpenMenu(null)}
              >
                Programs
              </NavLink>
            </div>
          </div>

          <div className={`nav-dropdown ${openMenu === "support" ? "open" : ""}`}>
            <button
              type="button"
              className="nav-dropdown-toggle"
              aria-expanded={openMenu === "support"}
              aria-haspopup="menu"
              onClick={() => toggleMenu("support")}
            >
              Support
            </button>
            <div className="nav-dropdown-menu" role="menu">
              <NavLink
                to="/intake"
                className={({ isActive }) => (isActive ? "active" : "")}
                onClick={() => setOpenMenu(null)}
              >
                Request support
              </NavLink>
              <NavLink
                to="/faq"
                className={({ isActive }) => (isActive ? "active" : "")}
                onClick={() => setOpenMenu(null)}
              >
                FAQ
              </NavLink>
            </div>
          </div>

          <NavLink to="/volunteer/busy-bees" className={({ isActive }) => (isActive ? "active" : "")}>
            Volunteer
          </NavLink>

          {/* Not a link until LilyPad is in the app stores; then point this at
              the download page and drop the Coming soon tag. */}
          <span className="nav-lilypad" title="LilyPad is coming soon">
            LilyPad <span className="nav-coming-soon">Coming soon</span>
          </span>

          <NavLink to="/donate" className={({ isActive }) => `donate-link${isActive ? " active" : ""}`}>
            Donate
          </NavLink>
        </nav>
      </div>
    </header>
  );
}
